import clsx from 'clsx';

/**
 * Touch-tuned action button. Visual recipe mirrors the desktop's *system*
 * action accent — amber-500 (#F59E0B) — which is the same hue used by the
 * desktop sidebar's active-nav state and the brand X-badge. This keeps the
 * "what's tappable / primary" vocabulary consistent across both apps.
 *
 *   - Variants: primary | secondary | danger
 *       primary   → bg-amber-500 hover:bg-amber-600 text-white
 *       secondary → bg-white hover:bg-gray-50 text-gray-700 border-gray-300
 *       danger    → bg-red-600 hover:bg-red-700 text-white border-red-600
 *   - Sizes: sm | md | lg → rounded → rounded-md → rounded-lg
 *   - Focus: ring-2 ring-offset-1
 *   - Loading: shows an inline spinner; disables the button
 *
 * Mobile-specific augmentation:
 *   - `min-h-touch` (44px) on every size so the touch target is always at
 *     least Apple HIG / Material minimum.
 *   - `fullWidth` defaults to true (mobile forms are typically column layouts
 *     where the primary action spans the row). Set to false for inline /
 *     split actions.
 *   - `active:scale-[0.98]` press feedback for touch.
 */

const variantClasses = {
  primary:
    'bg-amber-500 hover:bg-amber-600 active:bg-amber-600 text-white border border-amber-500 focus:ring-amber-500',
  secondary:
    'bg-white hover:bg-gray-50 active:bg-gray-100 text-gray-700 border border-gray-300 focus:ring-gray-400',
  danger:
    'bg-red-600 hover:bg-red-700 active:bg-red-700 text-white border border-red-600 focus:ring-red-500',
};

// All sizes are exactly 44px tall — Apple HIG / Material minimum touch target.
// Sizes differ by horizontal padding + font weight, not height. The previous
// `lg` was 48px (py-3 + 24px line-height) which felt over-tall on the Login
// splash; locking everything to h-11 keeps the touch target consistent and
// matches the 44px floor enforced by `min-h-touch` elsewhere.
const sizeClasses = {
  sm: 'h-11 px-3 text-small font-medium rounded',
  md: 'h-11 px-4 text-body  font-medium rounded-md',
  lg: 'h-11 px-6 text-body  font-semibold rounded-lg',
};

export default function BigButton({
  variant   = 'primary',
  size      = 'lg',
  loading   = false,
  disabled  = false,
  type      = 'button',
  fullWidth = true,
  className,
  children,
  ...rest
}) {
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={clsx(
        'inline-flex items-center justify-center gap-2 transition-colors duration-150',
        'min-h-touch active:scale-[0.98] transition-transform',
        'focus:outline-none focus:ring-2 focus:ring-offset-1',
        variantClasses[variant] ?? variantClasses.primary,
        sizeClasses[size]       ?? sizeClasses.lg,
        fullWidth && 'w-full',
        isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
      {...rest}
    >
      {loading && (
        <svg
          className="animate-spin h-4 w-4 shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}
      {children}
    </button>
  );
}
